/**
 * Membership Tiers CRUD — Life Member / Associate / Honorary etc.
 * Powers the dynamic filter tabs in the Member Directory below.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Layers } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const emptyTier = { key: "", label: "", description: "", rank: 100, active: true };

export default function MembershipTiersPanel({ org, accent = "#4EC1E4", onChange }) {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null);   // full row when editing, null when closed
  const [form, setForm] = useState(emptyTier);

  const refresh = async () => {
    if (!org?.id) return;
    try {
      const { data } = await api.get(`/orgs/${org.id}/membership-tiers`);
      setTiers(data.items || []);
    } catch (e) {
      console.warn("[MembershipTiersPanel]", e?.message);
    }
  };

  useEffect(() => { void refresh(); }, [org?.id]);

  const openNew = () => { setForm(emptyTier); setEditing({ id: null }); };
  const openEdit = (t) => {
    setForm({ key: t.key, label: t.label, description: t.description || "",
              rank: t.rank, active: t.active });
    setEditing(t);
  };

  const save = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      return toast.error("Key and label are required");
    }
    try {
      if (editing?.id) {
        await api.patch(`/orgs/${org.id}/membership-tiers/${editing.id}`, {
          label: form.label, description: form.description,
          rank: form.rank, active: form.active,
        });
        toast.success("Tier updated");
      } else {
        await api.post(`/orgs/${org.id}/membership-tiers`, form);
        toast.success("Tier created");
      }
      setEditing(null);
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tier '${t.label}'?`)) return;
    try {
      await api.delete(`/orgs/${org.id}/membership-tiers/${t.id}`);
      toast.success("Tier deleted");
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="tiers-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 inline-flex items-center gap-1">
            <Layers size={11}/> Membership Tiers
          </div>
          <h2 className="font-display text-xl uppercase tracking-tight">
            {tiers.length} defined
          </h2>
        </div>
        <Button onClick={openNew}
                className="h-8"
                style={{ background: accent, color: "#0b0b0b" }}
                data-testid="tier-create-btn">
          + New tier
        </Button>
      </div>

      {tiers.length === 0 ? (
        <div className="text-sm text-white/55 py-8 text-center">
          No tiers yet. Add designations like <span className="font-mono">Life Member</span>,
          <span className="font-mono"> Associate</span>, or
          <span className="font-mono"> Honorary</span> to categorise your directory.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 rounded-lg border border-white/10"
             data-testid="tiers-table">
          <table className="w-full text-left min-w-[520px]">
            <thead className="bg-white/5">
              <tr className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                <th className="px-3 py-2.5">Label</th>
                <th className="px-3 py-2.5">Key</th>
                <th className="px-3 py-2.5">Rank</th>
                <th className="px-3 py-2.5">Active</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.id} className="border-t border-white/5"
                    data-testid={`tier-row-${t.id}`}>
                  <td className="px-3 py-2.5 text-sm">{t.label}</td>
                  <td className="px-3 py-2.5 text-[11px] font-mono text-white/55">{t.key}</td>
                  <td className="px-3 py-2.5 text-xs">{t.rank}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {t.active ? <span className="text-emerald-300">yes</span>
                              : <span className="text-white/40">no</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button variant="ghost" className="h-7 px-2 text-white/60"
                            onClick={() => openEdit(t)}
                            data-testid={`tier-edit-${t.id}`}>
                      <Pencil size={13}/>
                    </Button>
                    <Button variant="ghost" className="h-7 px-2 text-rose-300"
                            onClick={() => remove(t)}
                            data-testid={`tier-delete-${t.id}`}>
                      <Trash2 size={13}/>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">
              {editing?.id ? "Edit Tier" : "New Tier"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Key {editing?.id && <span className="text-white/40 text-xs">(locked)</span>}</Label>
              <Input value={form.key}
                     disabled={!!editing?.id}
                     onChange={(e) => setForm({ ...form, key: e.target.value })}
                     placeholder="life_member"
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="tier-key-input"/>
            </div>
            <div>
              <Label>Label</Label>
              <Input value={form.label}
                     onChange={(e) => setForm({ ...form, label: e.target.value })}
                     placeholder="Life Member"
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="tier-label-input"/>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description}
                     onChange={(e) => setForm({ ...form, description: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rank (sort)</Label>
                <Input type="number" value={form.rank}
                       onChange={(e) => setForm({ ...form, rank: parseInt(e.target.value || "100", 10) })}
                       className="mt-1 bg-black/40 border-white/15"/>
              </div>
              <label className="flex items-center gap-2 pt-6 text-sm">
                <input type="checkbox" checked={form.active}
                       onChange={(e) => setForm({ ...form, active: e.target.checked })}/>
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-white text-black hover:bg-white/90"
                    data-testid="tier-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
